import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Leaf, Loader2 } from 'lucide-react';
import {
  enrollCarbonCharger, unenrollCarbonCharger, getDisplayErrorMessage,
  holdCarbonAccruals, releaseCarbonAccruals,
  fetchCarbonAccruals, fetchCarbonBatches, fetchCarbonChargers,
  fetchCarbonChargerReview, formatCents,
  type CarbonAccrualRow, type CarbonBatchRow, type CarbonChargerRow,
  type CarbonReviewSignal,
} from '@/lib/api';
import { ResponsiveTable, type Column } from '@/components/ui/ResponsiveTable';
import { Modal } from '@/components/ui/Modal';
import formStyles from '@/components/ui/Form.module.css';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './Carbon.module.css';

/**
 * Carbon program console (super_admin): claim-batch overview with the
 * margin arithmetic, the accrual ledger with the review filters
 * (jurisdiction / status / batch / session_source — the live-vs-
 * v1_history split a submission is reviewed by), and per-charger
 * registration state. Read-only — batch actions (submit / realize /
 * reject / payout) are API-driven for now; this page is where their
 * results are inspected.
 */

const JURISDICTIONS = ['ca_federal', 'ca_bc', 'us_ca'];
const STATUSES = ['accrued', 'submitted', 'realized', 'rejected'];
const CURRENCY: Record<string, string> = { ca_federal: 'CAD', ca_bc: 'CAD', us_ca: 'USD' };

function fmtDate(v: string | null): string {
  return v ? new Date(v).toLocaleDateString() : '—';
}

export function CarbonPage() {
  useRecentRoute('Carbon Credits');
  const [jurisdiction, setJurisdiction] = useState('');
  const [status, setStatus] = useState('');
  const [batch, setBatch] = useState('');
  const [source, setSource] = useState('');
  const [held, setHeld] = useState('');

  const queryClient = useQueryClient();
  const [enrollTarget, setEnrollTarget] = useState<CarbonChargerRow | null>(null);

  const batchesQ = useQuery({ queryKey: ['carbon', 'batches'], queryFn: fetchCarbonBatches });
  const accrualsQ = useQuery({
    queryKey: ['carbon', 'accruals', { jurisdiction, status, batch, source, held }],
    queryFn: () => fetchCarbonAccruals({
      jurisdiction: jurisdiction || undefined,
      status: status || undefined,
      batch: batch || undefined,
      source: source || undefined,
      held: held || undefined,
    }),
  });
  const chargersQ = useQuery({ queryKey: ['carbon', 'chargers'], queryFn: fetchCarbonChargers });

  const batchColumns: Column<CarbonBatchRow>[] = [
    { key: 'id', header: 'Batch', mobile: 'primary', cell: (b) => <span className={styles.mono}>{b._id}</span> },
    { key: 'rows', header: 'Rows', mobile: 'body', align: 'right', cell: (b) => b.rows },
    {
      key: 'kwh', header: 'kWh', mobile: 'body', align: 'right',
      cell: (b) => b.energy_kwh.toFixed(1),
    },
    {
      key: 'realized', header: 'Realized', mobile: 'secondary', align: 'right',
      cell: (b) => formatCents(b.realized_credit_value_cents, CURRENCY[b.jurisdiction] ?? 'CAD'),
    },
    {
      key: 'hostShare', header: 'Host share', mobile: 'body', align: 'right',
      cell: (b) => formatCents(b.realized_host_share_cents, CURRENCY[b.jurisdiction] ?? 'CAD'),
    },
    {
      key: 'margin', header: 'EVI margin', mobile: 'body', align: 'right',
      cell: (b) => (
        <span className={b.evi_margin_cents < 0 ? styles.negative : undefined}>
          {formatCents(b.evi_margin_cents, CURRENCY[b.jurisdiction] ?? 'CAD')}
        </span>
      ),
    },
    {
      key: 'shortfall', header: 'Shortfall', mobile: 'body', align: 'right',
      cell: (b) => b.shortfall_cents > 0
        ? <span className={styles.negative}>{formatCents(b.shortfall_cents, CURRENCY[b.jurisdiction] ?? 'CAD')}</span>
        : '—',
    },
    {
      key: 'unpaid', header: 'Unpaid rows', mobile: 'body', align: 'right',
      cell: (b) => b.unpaid_realized_rows > 0
        ? <span className={styles.warn}>{b.unpaid_realized_rows}</span> : '0',
    },
    {
      key: 'history', header: 'v1 history', mobile: 'body', align: 'right',
      cell: (b) => b.v1_history_rows > 0
        ? <span className={styles.historyBadge}>{b.v1_history_rows}</span> : '—',
    },
  ];

  const accrualColumns: Column<CarbonAccrualRow>[] = [
    { key: 'charger', header: 'Charger', mobile: 'primary', cell: (a) => <span className={styles.mono}>{a.charger_identity}</span> },
    { key: 'site', header: 'Site', mobile: 'secondary', cell: (a) => a.site_name },
    { key: 'ended', header: 'Session end', mobile: 'body', cell: (a) => fmtDate(a.session_ended_at) },
    { key: 'kwh', header: 'kWh', mobile: 'body', align: 'right', cell: (a) => a.energy_kwh.toFixed(2) },
    { key: 'status', header: 'Status', mobile: 'body', cell: (a) => <span className={`${styles.statusBadge} ${styles[`status_${a.status}`] ?? ''}`}>{a.status}</span> },
    {
      key: 'source', header: 'Source', mobile: 'body',
      cell: (a) => a.session_source === 'v1_history'
        ? <span className={styles.historyBadge}>v1 history</span>
        : <span className={styles.dim}>live</span>,
    },
    {
      key: 'estimate', header: 'Est. share', mobile: 'body', align: 'right',
      cell: (a) => formatCents(a.host_share_cents, CURRENCY[a.jurisdiction] ?? 'CAD'),
    },
    {
      key: 'realized', header: 'Realized share', mobile: 'body', align: 'right',
      cell: (a) => a.realized_host_share_cents !== null
        ? formatCents(a.realized_host_share_cents, CURRENCY[a.jurisdiction] ?? 'CAD') : '—',
    },
    {
      key: 'hold', header: 'Hold', mobile: 'body',
      cell: (a) => (a.review_hold
        ? <span className={styles.holdBadge} title={a.hold_reason ?? undefined}>HELD</span>
        : <span className={styles.dim}>—</span>),
    },
    { key: 'batch', header: 'Batch', mobile: 'body', cell: (a) => a.claim_batch_id ?? '—' },
    {
      key: 'actions', header: '', mobile: 'body',
      cell: (a) => (
        (a.status === 'realized' || a.status === 'rejected')
          ? <span className={styles.dim}>—</span>
          : (
            <button
              type="button"
              className={styles.rowBtn}
              disabled={holdBusy}
              onClick={(e) => { e.stopPropagation(); void (a.review_hold ? onRelease(a) : onHold(a)); }}
            >
              {a.review_hold ? 'Release' : 'Hold'}
            </button>
          )
      ),
    },
  ];

  const chargerColumns: Column<CarbonChargerRow>[] = [
    { key: 'identity', header: 'Charger', mobile: 'primary', cell: (c) => <span className={styles.mono}>{c.identity}</span> },
    { key: 'site', header: 'Site', mobile: 'secondary', cell: (c) => c.site_name },
    {
      key: 'eligible', header: 'Eligible', mobile: 'body',
      cell: (c) => c.eligible
        ? <span className={styles.ok}>Yes</span>
        : <span className={styles.dim}>{c.excluded_reason ? `No — ${c.excluded_reason}` : 'No'}</span>,
    },
    { key: 'jurisdiction', header: 'Jurisdiction', mobile: 'body', cell: (c) => c.jurisdiction ?? '—' },
    { key: 'registered', header: 'Registered', mobile: 'body', cell: (c) => fmtDate(c.registered_at) },
    { key: 'partnerId', header: 'Partner reg. id', mobile: 'body', cell: (c) => c.partner_registration_id ?? '—' },
    {
      key: 'coord', header: 'Coordinate', mobile: 'body',
      cell: (c) => c.has_coordinate
        ? <span className={styles.ok}>✓</span>
        : <span className={styles.warn}>missing</span>,
    },
  ];

  // Hold / release straight from the ledger. Prompt-driven rather
  // than a modal: a hold is a small, frequent, reversible act, and the
  // reason is mandatory server-side so it cannot be skipped here.
  const [holdBusy, setHoldBusy] = useState(false);
  const refreshCarbon = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['carbon'] });
  };
  const onHold = async (row: CarbonAccrualRow): Promise<void> => {
    const reason = window.prompt(
      `Hold accrual for ${row.charger_identity}?

It stays visible and undeleted, and is kept out of the next submission until released.

Reason (required):`,
    );
    if (!reason?.trim()) return;
    setHoldBusy(true);
    try {
      const res = await holdCarbonAccruals({ accrual_ids: [row._id], reason: reason.trim() });
      if (res.note) window.alert(res.note);
      await refreshCarbon();
    } catch (err) {
      window.alert(await getDisplayErrorMessage(err, 'Hold failed.'));
    } finally { setHoldBusy(false); }
  };
  const onRelease = async (row: CarbonAccrualRow): Promise<void> => {
    if (!window.confirm(`Release the hold on this ${row.charger_identity} accrual? It becomes eligible for the next submission.`)) return;
    setHoldBusy(true);
    try {
      await releaseCarbonAccruals({ accrual_ids: [row._id] });
      await refreshCarbon();
    } catch (err) {
      window.alert(await getDisplayErrorMessage(err, 'Release failed.'));
    } finally { setHoldBusy(false); }
  };

  // Registration table: enrolled chargers first, then the rest.
  const chargerRows = [...(chargersQ.data ?? [])].sort((a, b) =>
    Number(b.eligible) - Number(a.eligible) || a.identity.localeCompare(b.identity));

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerIcon}><Leaf size={18} /></div>
        <div>
          <h1 className={styles.title}>Carbon Credits</h1>
          <p className={styles.subtitle}>Claim batches, the accrual ledger, and charger registration</p>
        </div>
      </div>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Claim batches</h2>
        {/* Held rows never receive a claim_batch_id, so they cannot show
            inside a batch row — this is how a reviewer sees what the next
            submission will leave out. */}
        {(batchesQ.data?.held_excluded ?? []).length > 0 && (
          <div className={styles.heldBanner} role="status">
            <strong>Held out of submission:</strong>{' '}
            {(batchesQ.data?.held_excluded ?? []).map((h) => (
              <span key={h.jurisdiction} className={styles.heldBannerItem}>
                {h.jurisdiction} — {h.rows} row{h.rows === 1 ? '' : 's'},{' '}
                {h.energy_kwh.toFixed(1)} kWh,{' '}
                {formatCents(h.host_share_cents, CURRENCY[h.jurisdiction] ?? 'CAD')} est. share
              </span>
            ))}
          </div>
        )}
        {batchesQ.isLoading ? <p className={styles.loading}>Loading…</p>
          : batchesQ.isError ? <p className={styles.error}>Could not load batches.</p>
            : (
              <ResponsiveTable
                columns={batchColumns}
                rows={batchesQ.data?.batches ?? []}
                rowKey={(b) => b._id}
                onRowClick={(b) => setBatch(b._id)}
                emptyMessage="No claim batches yet."
              />
            )}
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Accruals</h2>
        <div className={styles.filterRow}>
          <select className={styles.select} value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)} aria-label="Jurisdiction">
            <option value="">All jurisdictions</option>
            {JURISDICTIONS.map((j) => <option key={j} value={j}>{j}</option>)}
          </select>
          <select className={styles.select} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className={styles.select} value={source} onChange={(e) => setSource(e.target.value)} aria-label="Source">
            <option value="">All sources</option>
            <option value="live">live</option>
            <option value="v1_history">v1 history</option>
          </select>
          <select className={styles.select} value={held} onChange={(e) => setHeld(e.target.value)} aria-label="Review hold">
            <option value="">Held and not held</option>
            <option value="true">Held only</option>
            <option value="false">Not held</option>
          </select>
          <select className={styles.select} value={batch} onChange={(e) => setBatch(e.target.value)} aria-label="Batch">
            <option value="">All batches</option>
            {(batchesQ.data?.batches ?? []).map((b) => <option key={b._id} value={b._id}>{b._id}</option>)}
          </select>
        </div>
        {accrualsQ.isLoading ? <p className={styles.loading}>Loading…</p>
          : accrualsQ.isError ? <p className={styles.error}>Could not load accruals.</p>
            : (
              <>
                {accrualsQ.data!.truncated && (
                  <p className={styles.truncated}>
                    Showing {accrualsQ.data!.accruals.length} of {accrualsQ.data!.total} — narrow the filters for the rest.
                  </p>
                )}
                <ResponsiveTable
                  columns={accrualColumns}
                  rows={accrualsQ.data!.accruals}
                  rowKey={(a) => a._id}
                  emptyMessage="No accruals match these filters."
                />
              </>
            )}
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Charger registration</h2>
        {chargersQ.isLoading ? <p className={styles.loading}>Loading…</p>
          : chargersQ.isError ? <p className={styles.error}>Could not load chargers.</p>
            : (
              <ResponsiveTable
                columns={chargerColumns}
                rows={chargerRows}
                rowKey={(c) => c.identity}
                onRowClick={(c) => setEnrollTarget(c)}
                emptyMessage="No chargers."
              />
            )}
        <p className={styles.truncated}>
          Click a charger to enroll or un-enroll it in the carbon program.
        </p>
      </section>

      {enrollTarget && (
        <EnrollModal
          charger={enrollTarget}
          onClose={() => setEnrollTarget(null)}
          onDone={() => {
            setEnrollTarget(null);
            void queryClient.invalidateQueries({ queryKey: ['carbon', 'chargers'] });
          }}
        />
      )}
    </div>
  );
}

/**
 * The contradiction panel — context + cross-checks at the moment of
 * the enrollment decision. It INFORMS, it does not block: only the
 * server's missing-field gate refuses an enrollment.
 *
 * THREE STATES, never blurred. 'unknown' renders in its own muted
 * style with the reason it is unknown, deliberately NOT the green of
 * 'agrees' — a charger with no captured IP must never read as a
 * charger whose IP checks out. Several signals are structurally
 * unknown until later builds (charger-IP capture, IP-to-country
 * lookup, the geocoder in deployed envs), and they say so.
 */
function ContradictionPanel({ identity }: { identity: string }) {
  const reviewQ = useQuery({
    queryKey: ['carbon', 'review', identity],
    queryFn: () => fetchCarbonChargerReview(identity),
  });

  if (reviewQ.isLoading) return <p className={styles.loading}>Loading review signals…</p>;
  if (reviewQ.isError || !reviewQ.data) {
    return <p className={styles.error}>Could not load review signals.</p>;
  }
  const r = reviewQ.data;
  const disagreeCount = r.signals.filter((s) => s.state === 'disagrees').length;
  const unknownCount = r.signals.filter((s) => s.state === 'unknown').length;

  return (
    <div className={styles.reviewPanel}>
      <div className={styles.reviewHeader}>
        <span>Review context</span>
        <span className={styles.reviewCounts}>
          {disagreeCount > 0 && <span className={styles.negative}>{disagreeCount} contradiction{disagreeCount === 1 ? '' : 's'}</span>}
          {disagreeCount > 0 && unknownCount > 0 && ' · '}
          {unknownCount > 0 && <span className={styles.dim}>{unknownCount} unknown</span>}
          {disagreeCount === 0 && unknownCount === 0 && <span className={styles.ok}>all signals agree</span>}
        </span>
      </div>

      <dl className={styles.factGrid}>
        <dt>Created via</dt><dd>{r.created_via}</dd>
        <dt>Owner</dt>
        <dd>{r.owner_email ? `${r.owner_name ?? '(no name)'} · ${r.owner_email}` : <span className={styles.dim}>none recorded (admin-created)</span>}</dd>
        <dt>First session</dt>
        <dd>{r.first_seen_at ? new Date(r.first_seen_at).toLocaleDateString() : <span className={styles.dim}>never charged</span>}</dd>
        <dt>Lifetime</dt>
        <dd>{r.lifetime_kwh.toFixed(1)} kWh over {r.session_count} session{r.session_count === 1 ? '' : 's'}</dd>
      </dl>

      <ul className={styles.signalList}>
        {r.signals.map((s) => <SignalRow key={s.key} signal={s} />)}
      </ul>
    </div>
  );
}

function SignalRow({ signal }: { signal: CarbonReviewSignal }) {
  const cls = signal.state === 'agrees' ? styles.signalAgrees
    : signal.state === 'disagrees' ? styles.signalDisagrees
      : styles.signalUnknown;
  const mark = signal.state === 'agrees' ? '✓'
    : signal.state === 'disagrees' ? '✕' : '—';
  return (
    <li className={`${styles.signalRow} ${cls}`}>
      <span className={styles.signalMark} aria-hidden>{mark}</span>
      <div className={styles.signalBody}>
        <span className={styles.signalLabel}>
          {signal.label}
          <span className={styles.signalState}>{signal.state === 'unknown' ? 'no data' : signal.state}</span>
        </span>
        <span className={styles.signalValue}>{signal.value}</span>
        <span className={styles.signalDetail}>{signal.detail}</span>
      </div>
    </li>
  );
}

/**
 * Enrollment gate dialog. The server refuses with the SPECIFIC list of
 * missing partner-required fields (manufacturer, model, serial,
 * connector type, per-charger coordinate) — that message renders
 * verbatim here, so a refusal doubles as the charger's remaining
 * survey worksheet. registered_at is backdatable: the credit cycle is
 * annual, and the sweeper's epoch guard owns the how-far-back rule.
 */
function EnrollModal({ charger, onClose, onDone }: {
  charger: CarbonChargerRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [jurisdiction, setJurisdiction] = useState(charger.jurisdiction ?? 'ca_federal');
  const [registeredAt, setRegisteredAt] = useState(
    charger.registered_at ? charger.registered_at.slice(0, 10) : new Date().toISOString().slice(0, 10),
  );
  const [partnerId, setPartnerId] = useState(charger.partner_registration_id ?? '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Defaults FALSE: the safe default is to leave already-earned
  // accruals alone. Holding is the deliberate act.
  const [holdExisting, setHoldExisting] = useState(false);

  const submitEnroll = async (): Promise<void> => {
    setBusy(true);
    setError('');
    try {
      await enrollCarbonCharger(charger.identity, {
        jurisdiction,
        registered_at: registeredAt,
        partner_registration_id: partnerId.trim() || undefined,
      });
      onDone();
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Enrollment failed. Please try again.'));
      setBusy(false);
    }
  };

  const submitUnenroll = async (): Promise<void> => {
    setBusy(true);
    setError('');
    try {
      await unenrollCarbonCharger(charger.identity, {
        excluded_reason: reason.trim() || undefined,
        // EXPLICIT, never inferred. Un-enrolling for a benign reason
        // (decommissioned, site sold, owner opted out) must not sweep
        // up legitimate earnings, so the operator chooses here.
        hold_existing_accruals: holdExisting,
      });
      onDone();
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Un-enrollment failed. Please try again.'));
      setBusy(false);
    }
  };

  return (
    <Modal isOpen title={`Carbon enrollment — ${charger.identity}`} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p className={styles.truncated}>
          {charger.site_name} · currently {charger.eligible ? 'ENROLLED' : 'not enrolled'}
          {charger.has_coordinate ? '' : ' · no surveyed coordinate'}
        </p>

        <ContradictionPanel identity={charger.identity} />

        <div className={formStyles.field}>
          <label className={formStyles.label}>Jurisdiction</label>
          <select
            className={formStyles.input}
            value={jurisdiction}
            onChange={(e) => setJurisdiction(e.target.value)}
          >
            <option value="ca_federal">ca_federal (CFR)</option>
            <option value="ca_bc">ca_bc (CFR + BC-LCFS)</option>
            <option value="us_ca">us_ca (LCFS)</option>
          </select>
        </div>
        <div className={formStyles.field}>
          <label className={formStyles.label}>Registered date (backdatable)</label>
          <input
            className={formStyles.input}
            type="date"
            value={registeredAt}
            onChange={(e) => setRegisteredAt(e.target.value)}
          />
        </div>
        <div className={formStyles.field}>
          <label className={formStyles.label}>Partner registration id (optional)</label>
          <input
            className={formStyles.input}
            value={partnerId}
            onChange={(e) => setPartnerId(e.target.value)}
            placeholder="assigned by the credit partner"
          />
        </div>

        {error && <div className={styles.error} role="alert">{error}</div>}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center' }}>
          {charger.eligible ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  className={formStyles.input}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="un-enroll reason"
                  style={{ width: 180 }}
                />
                <button type="button" className={formStyles.btnSecondary} onClick={submitUnenroll} disabled={busy}>
                  Un-enroll
                </button>
              </div>
              {/* The choice, presented AT the un-enrollment moment.
                  Unchecked by default — stopping future accrual is the
                  action; pulling back earned accruals is a separate
                  judgement that only applies when the reason is
                  suspicion rather than housekeeping. */}
              <label style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 12 }}>
                <input
                  type="checkbox"
                  checked={holdExisting}
                  onChange={(e) => setHoldExisting(e.target.checked)}
                  disabled={busy}
                />
                <span>
                  Also hold this charger&rsquo;s existing accrued rows
                  <span className={styles.dim}>
                    {' '}— keeps them out of the next submission. Leave unchecked
                    if this un-enrollment is routine (decommissioned, site sold,
                    owner opted out).
                  </span>
                </span>
              </label>
            </div>
          ) : <span />}
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className={formStyles.btnSecondary} onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="button" className={formStyles.btnPrimary} onClick={submitEnroll} disabled={busy}>
              {busy ? <><Loader2 size={13} className={styles.spin} /> Saving…</> : (charger.eligible ? 'Update enrollment' : 'Enroll')}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
